import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

import { AuthService } from "../../insa-services";
import { appConfig } from "../../../insaweb.config";
import {
  Watchlist,
  ControlTableSecurity,
  isFavouritesWatchlist,
} from "./control-table.models";

export interface TraderSecurityDtoModel {
  SecIndex: number;
  SecNumber: string;
  SecISIN: string;
  SecStockExchange: number;
  SecSymbol: string;
  SecName: string;
  SecCurrSymbol: string;
  FxRate: number;
  StockExchangeCode: string;
  StockExchangeName: string;
  EODHDStockExchangeCode: string;
  SecType: number;
  SecCurr: number;
  SecFactor: number;
  SecContractSize: number;
  fromDate?: string;
  toDate?: string;
}

export interface EODHDRealTimeResponseModel {
  price: number | null;
  bid: number | null;
  bidQuantity: number | null;
  ask: number | null;
  askQuantity: number | null;
  level: number | null;
  lastClose: number | null;
  open: number | null;
  high: number | null;
  low: number | null;
  volume: number | null;
  tradingPhase: string | null;
  priceRange: string | null;
  currency: string | null;
}

export interface TraderOrderResponseModel {
  success: boolean;
  description: string;
}

export interface TraderCheckOrderResponseModel {
  success: boolean;
  description: string;
  amounts: { [key: string]: number };
  commission?: number;
  expenses?: number;
  otherExpenses?: number;
}

export interface WatchlistSecurityQuoteModel {
  security: TraderSecurityDtoModel;
  L1Values: EODHDRealTimeResponseModel | null;
}

@Injectable()
export class ControlTableService {
  private readonly apiBase = appConfig.insaServiceAddress + "/Trader";
  private readonly watchlists$ = new BehaviorSubject<Watchlist[]>([]);

  constructor(
    private _http: HttpClient,
    private _authService: AuthService,
  ) {}

  private headers() {
    return this._authService.getAuthHeaders("application/json");
  }

  get watchlists(): Observable<Watchlist[]> {
    return this.watchlists$.asObservable();
  }

  get watchlistsSnapshot(): Watchlist[] {
    return this.watchlists$.value;
  }

  loadWatchlists(): void {
    this._http
      .get<Watchlist[]>(this.apiBase + "/TraderWatchlists", {
        headers: this.headers(),
      })
      .pipe(catchError(() => of([])))
      .subscribe((list) => this.watchlists$.next(list ?? []));
  }

  createWatchlist(name: string): Observable<Watchlist | null> {
    return this._http
      .post<Watchlist>(
        this.apiBase + "/TraderWatchlistInsert",
        { WatchlistName: name },
        { headers: this.headers() },
      )
      .pipe(catchError(() => of(null)));
  }

  renameWatchlist(
    id: number,
    name: string,
  ): Observable<TraderOrderResponseModel> {
    return this._http
      .put<TraderOrderResponseModel>(
        this.apiBase + "/TraderWatchlistUpdate",
        { WatchlistID: id, WatchlistName: name },
        { headers: this.headers() },
      )
      .pipe(
        catchError((err) =>
          of({ success: false, description: err?.message ?? "error" }),
        ),
      );
  }

  deleteWatchlist(id: number): Observable<TraderOrderResponseModel> {
    return this._http
      .request<TraderOrderResponseModel>(
        "delete",
        this.apiBase + "/TraderWatchlistDelete",
        { headers: this.headers(), body: { WatchlistID: id } },
      )
      .pipe(
        catchError((err) =>
          of({ success: false, description: err?.message ?? "error" }),
        ),
      );
  }

  watchlistSecurities(
    watchlistID: number,
  ): Observable<WatchlistSecurityQuoteModel[]> {
    return this._http
      .get<WatchlistSecurityQuoteModel[]>(
        this.apiBase + "/TraderWatchlistSecurities",
        {
          headers: this.headers(),
          params: { watchlistID: watchlistID.toString() },
        },
      )
      .pipe(catchError(() => of([])));
  }

  // Favoriti (WatchlistID 0) nisu prava watchlista - idu preko istih endpointa
  // kao zvjezdica na traderu, pa su promjene vidljive i tamo.
  addSecurity(
    watchlistID: number,
    security: TraderSecurityDtoModel | ControlTableSecurity,
  ): Observable<TraderOrderResponseModel> {
    const request = isFavouritesWatchlist(watchlistID)
      ? this._http.post<TraderOrderResponseModel>(
          this.apiBase + "/TraderFavouritesInsert",
          security,
          { headers: this.headers() },
        )
      : this._http.post<TraderOrderResponseModel>(
          this.apiBase + "/TraderWatchlistSecurityInsert",
          { WatchlistID: watchlistID, SecIndex: security.SecIndex },
          { headers: this.headers() },
        );
    return request.pipe(
      catchError((err) =>
        of({ success: false, description: err?.message ?? "error" }),
      ),
    );
  }

  removeSecurity(
    watchlistID: number,
    security: ControlTableSecurity,
  ): Observable<TraderOrderResponseModel> {
    const favourites = isFavouritesWatchlist(watchlistID);
    return this._http
      .request<TraderOrderResponseModel>(
        "delete",
        this.apiBase +
          (favourites
            ? "/TraderFavouritesDelete"
            : "/TraderWatchlistSecurityDelete"),
        {
          headers: this.headers(),
          body: favourites
            ? security
            : { WatchlistID: watchlistID, SecIndex: security.SecIndex },
        },
      )
      .pipe(
        catchError((err) =>
          of({ success: false, description: err?.message ?? "error" }),
        ),
      );
  }

  searchSecurities(
    filter: string,
    currency: string,
  ): Observable<TraderSecurityDtoModel[]> {
    return this._http
      .get<TraderSecurityDtoModel[]>(this.apiBase + "/TraderSecurities", {
        headers: this.headers(),
        params: { filter: filter.trim(), currency },
      })
      .pipe(catchError(() => of([])));
  }

  realTimeL1(
    security: ControlTableSecurity,
  ): Observable<EODHDRealTimeResponseModel[]> {
    return this._http
      .post<EODHDRealTimeResponseModel[]>(
        this.apiBase + "/TraderRealTimePricesL1",
        security,
        { headers: this.headers() },
      )
      .pipe(catchError(() => of([])));
  }

  realTimeL2(
    security: ControlTableSecurity,
  ): Observable<EODHDRealTimeResponseModel[]> {
    return this._http
      .post<EODHDRealTimeResponseModel[]>(
        this.apiBase + "/TraderRealTimePricesL2",
        security,
        { headers: this.headers() },
      )
      .pipe(catchError(() => of([])));
  }

  cashBalanceForSecurity(
    security: ControlTableSecurity,
    currency: string,
  ): Observable<number> {
    return this._http
      .post<number>(this.apiBase + "/CashBalanceForSecurity", security, {
        headers: this.headers(),
        params: { currency },
      })
      .pipe(catchError(() => of(0)));
  }

  positionQuantityForSecurity(
    security: ControlTableSecurity,
  ): Observable<number> {
    return this._http
      .post<number>(this.apiBase + "/PositionQuantityForSecurity", security, {
        headers: this.headers(),
      })
      .pipe(catchError(() => of(0)));
  }

  // Kao trader: false = provjera raspoloživog stanja za partnera isključena.
  checkCashBalanceForUser(): Observable<boolean> {
    return this._http
      .get<boolean>(this.apiBase + "/CheckCashBalanceForUser", {
        headers: this.headers(),
      })
      .pipe(catchError(() => of(true)));
  }

  secReservationPercent(secIndex: number): Observable<number> {
    return this._http
      .get<number>(this.apiBase + "/GetSecReservationPercent", {
        headers: this.headers(),
        params: { secIndex: secIndex.toString() },
      })
      .pipe(catchError(() => of(1)));
  }

  allowedTransactionTypes(secStockExchange: number): Observable<number[]> {
    return this._http
      .get<number[]>(this.apiBase + "/GetOrderTranTypeForSE", {
        headers: this.headers(),
        params: { secStockExchange: secStockExchange.toString() },
      })
      .pipe(catchError(() => of([])));
  }

  allowedOrderTypes(
    secStockExchange: number,
    transactionType: number,
  ): Observable<number[]> {
    return this._http
      .get<number[]>(this.apiBase + "/GetOrderTypeForSEAndTranType", {
        headers: this.headers(),
        params: {
          secStockExchange: secStockExchange.toString(),
          orderTransactionType: transactionType.toString(),
        },
      })
      .pipe(catchError(() => of([])));
  }

  orderCheck(order: any): Observable<TraderCheckOrderResponseModel> {
    return this._http.post<TraderCheckOrderResponseModel>(
      this.apiBase + "/TraderOrderCheck",
      order,
      { headers: this.headers() },
    );
  }

  orderInsert(order: any): Observable<TraderOrderResponseModel> {
    return this._http.post<TraderOrderResponseModel>(
      this.apiBase + "/TraderOrderInsert",
      order,
      { headers: this.headers() },
    );
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table.service.ts